/**
 * 案件一覧の「1{案件}ごと登録」で開くモーダル (利用者指示 2026-10-30)。見た目はブランド新規作成モーダル (CreateBrandModal) の 1 段目に揃える。
 *
 * - 登録方法の 2 択をモーダルで出す。「手動で入力する」はすぐ登録ページ (/products/manual/new) のフォームへ進む
 * - 「URLから{案件}情報を生成する」は、クロールと解析が終わるまでモーダルの中で読み取りパネルを出す (生成を押したらカードをやめ、
 *   パネルだけで描く)。完了したら登録ページへ進み、ページの読み取りパネルが同じジョブを引き継いで結果をフォームへ入れる
 *   (引き継ぎ情報 = localStorage の resume を、モーダルで発行した draftId のキーで残す)
 * - 「読み取りを待たずに入力を始める」は実行中のまま登録ページへ進む (ページのパネルが続きを待つ)
 * - 実行中は ✕ を押せない (閉じると結果を受け取る画面が無くなる・ブランド新規作成の生成中と同じ)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { brandConfig } from '../../../config/brands';
import { apiClient } from '../../../services/apiClient';
import { newDraftId } from '../../../features/products/productDraft';
import { loadUrlSummaryResume, saveUrlSummaryResume, type UrlSummaryFormSnapshot, type UrlSummaryResume, type UrlSummarySource } from '../../../features/products/urlSummary';
import { useUrlSummaryResumeScope } from '../../../features/products/useUrlSummaryResumeScope';
import { entryStateOf, type ProductEntryState } from '../../../features/products/productEntry';
import { ProductEntryChoices } from './ProductEntryChoices';
import { ProductUrlSummaryPanel } from './ProductUrlSummaryPanel';

/** 登録ページへ渡す遷移の state (features/products/productEntry.ts)。ManualProductNewPage が新しい下書きを開くときに読む */
export type { ProductEntryState } from '../../../features/products/productEntry';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/** 読み取りの下書きは空のフォームから始める (提案は全部入れる) */
const EMPTY_FORM: UrlSummaryFormSnapshot = {
  title: '',
  description: '',
  brand_name: '',
  brand_id: '',
  product_type: '',
  category: '',
  industry_categories: [],
};

export function ProductEntryModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const scope = useUrlSummaryResumeScope();
  // URL 経路を始めたときに発行する。登録ページの ?draft= にそのまま渡し、読み取りの引き継ぎキーにする
  // (元は UrlSummarySource で持つ。動画からの下書きは動画エディタから登録ページへ直接進むので、このモーダルでは URL だけ)
  const [reading, setReading] = useState<{ draftId: string; source: UrlSummarySource } | null>(null);
  const [running, setRunning] = useState(false);
  const finishedRef = useRef<UrlSummaryResume | null>(null);
  const [finished, setFinished] = useState(false);

  const { data: brandsData } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => (await apiClient.get('/brands')).data,
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  });
  const brands = useMemo<{ id: string; brand_name: string }[]>(() => brandsData?.brands ?? [], [brandsData]);

  useEffect(() => {
    if (isOpen) return;
    setReading(null);
    setRunning(false);
    setFinished(false);
    finishedRef.current = null;
  }, [isOpen]);

  const goToPage = useCallback((state: ProductEntryState, draftId?: string) => {
    navigate(draftId ? `/products/manual/new?draft=${encodeURIComponent(draftId)}` : '/products/manual/new', { state });
  }, [navigate]);

  // 完了: パネルは反映 (onApply) の直後に引き継ぎ情報を消すので、反映の時点で控えておき、消えたあとに書き戻してからページへ進む
  useEffect(() => {
    if (!finished || !reading || !scope) return;
    if (finishedRef.current) saveUrlSummaryResume(scope, reading.draftId, finishedRef.current);
    goToPage(entryStateOf(reading.source), reading.draftId);
  }, [finished, reading, scope, goToPage]);

  const getForm = useCallback(() => EMPTY_FORM, []);
  const handleApply = useCallback(() => {
    if (!scope || !reading) return;
    finishedRef.current = loadUrlSummaryResume(scope, reading.draftId);
    setFinished(true);
  }, [scope, reading]);

  if (!isOpen) return null;

  return createPortal(
    // 入力内容の消失を防ぐため、オーバーレイのクリックでは閉じない (✕ ボタンのみ)
    <div className="modal-overlay">
      <div className="modal-card modal-card--lg" role="dialog" aria-modal="true" aria-labelledby="product-entry-modal-title">
        <div className="modal-header">
          <h3 className="modal-title product-entry-modal-title" id="product-entry-modal-title">
            {/* 案件一覧の見出しと同じ Streamline のアイコン (mask で見出しの文字色に塗る) */}
            <span className="product-entry-modal-title-icon" aria-hidden="true" />
            {brandConfig.labels.productShort}を登録
          </h3>
          <button className="modal-close-button" onClick={onClose} type="button" aria-label="閉じる" disabled={running || finished}>
            ✕
          </button>
        </div>
        <div className="product-entry-modal-body">
          {reading ? (
            <>
              <ProductUrlSummaryPanel
                draftId={reading.draftId}
                hideTitle
                getForm={getForm}
                brands={brands}
                onApply={handleApply}
                onRunningChange={setRunning}
                initialUrl={reading.source.kind === 'url' ? reading.source.url : undefined}
                source={reading.source.kind === 'video' ? reading.source : undefined}
                autoStart
                autoApply
                hideRereadAction
              />
              {running && (
                <div className="new-product-reading-actions">
                  {/* 読み取りは続けたまま登録ページのフォームを開く (ページのパネルが続きを待ち、完了すれば未入力の項目に入る) */}
                  <button type="button" className="new-product-text-link" onClick={() => goToPage(entryStateOf(reading.source, false), reading.draftId)}>
                    <LogOut size={14} aria-hidden="true" />
                    読み取りを待たずに入力を始める
                  </button>
                </div>
              )}
            </>
          ) : (
            <ProductEntryChoices
              disabled={!scope}
              onStartUrl={(url) => setReading({ draftId: newDraftId(), source: { kind: 'url', url } })}
              onStartManual={() => goToPage({ entry: 'manual' })}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
